<script setup>
// X159 (Grant 2026-10-27 on Club settings › Logo: "you can't enlarge, pan and zoom the current club logo here"; he
// chose: just view it larger, no editing). The one image viewer: a TlDialog sized to the screen showing the full-size
// image fitted at open. Zoom with the wheel or a trackpad pinch, a two-finger pinch on touch, the + / − buttons and the
// keyboard (+ − 0, arrows pan); a double-click or double-tap toggles fit ↔ 2× about that point; drag or one-finger pan,
// bounded so the image can never be thrown off screen (utils/imageViewer.js). Esc and ✕ close (no foot: the room is the picture's). MessageBubble's photo
// lightbox uses it too, so images behave the same everywhere.
//
//   <tl-image-viewer v-model="open" :src="fullUrl" :fallback-src="largeUrl" alt="Club2 logo" title="Club logo"/>
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import {
  ZOOM_MAX, ZOOM_MIN, ZOOM_STEP, clampZoom, fitSize, initialState, isDoubleTap, keyZoom, panBy, pinchZoom,
  toggleZoom, wheelZoom, zoomAt, zoomLabel,
} from '~/utils/imageViewer';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  /** The largest URL there is for the picture (the upload's original). */
  src: {type: String, default: null},
  /** Shown instead when `src` fails to load (an older upload with no original: its large_ copy). */
  fallbackSrc: {type: String, default: null},
  alt: {type: String, default: ''},
  title: {type: String, default: 'Image'},
  /** Words under the picture (a chat photo's caption). */
  caption: {type: String, default: null},
});
const emit = defineEmits(['update:modelValue']);

const stage = ref(null);
const stageSize = ref({width: 0, height: 0});
const natural = ref({width: 0, height: 0});
const loaded = ref(false);
const failed = ref(false);
const usingFallback = ref(false);
const state = ref(initialState());
// Button / keyboard / double-tap zooms glide; wheel, drag and pinch follow the hand with no transition.
const animate = ref(false);
const dragging = ref(false);

const shownSrc = computed(() => (usingFallback.value && props.fallbackSrc) ? props.fallbackSrc : props.src);
const fit = computed(() => fitSize(natural.value.width, natural.value.height, stageSize.value.width, stageSize.value.height));
const zoom = computed(() => state.value.zoom);
const label = computed(() => zoomLabel(zoom.value));
const canZoomIn = computed(() => loaded.value && zoom.value < ZOOM_MAX - 0.001);
const canZoomOut = computed(() => loaded.value && zoom.value > ZOOM_MIN + 0.001);
const imageStyle = computed(() => ({
  width: fit.value.width ? `${fit.value.width}px` : null,
  height: fit.value.height ? `${fit.value.height}px` : null,
  transform: `translate(${state.value.pan.x}px, ${state.value.pan.y}px) scale(${state.value.zoom})`,
}));

const reset = () => {
  state.value = initialState();
  animate.value = false;
};
const onLoad = (e) => {
  natural.value = {width: e?.target?.naturalWidth || 0, height: e?.target?.naturalHeight || 0};
  loaded.value = true;
  failed.value = false;
  reset();
};
const onError = () => {
  if (!usingFallback.value && props.fallbackSrc && props.fallbackSrc !== props.src) {
    usingFallback.value = true;
    return;
  }
  failed.value = true;
  loaded.value = false;
};

// ── Stage size (ResizeObserver; the fit follows the window) ──
let observer = null;
const measure = () => {
  const el = stage.value;
  if (!el) return;
  stageSize.value = {width: el.clientWidth, height: el.clientHeight};
  state.value = {zoom: state.value.zoom, pan: panBy(state.value, 0, 0, fit.value, stageSize.value.width, stageSize.value.height).pan};
};
const observe = () => {
  if (typeof ResizeObserver === 'undefined' || !stage.value) return;
  observer?.disconnect();
  observer = new ResizeObserver(measure);
  observer.observe(stage.value);
};
onBeforeUnmount(() => observer?.disconnect());
// The dialog mounts its content on the first open, a tick or more after modelValue flips: attach when the stage is there.
watch(stage, (el) => {
  if (!el || !props.modelValue) return;
  measure();
  observe();
  el.focus?.({preventScroll: true});
});

watch(() => props.modelValue, async (open) => {
  if (!open) {
    observer?.disconnect();
    observer = null;
    pointers.clear();
    lastTap = null;
    return;
  }
  loaded.value = false;
  failed.value = false;
  usingFallback.value = false;
  reset();
  await nextTick();
  if (!stage.value) return; // not mounted yet: the stage watch takes over
  measure();
  observe();
  stage.value.focus?.({preventScroll: true});
});
watch(() => props.src, () => {
  loaded.value = false;
  failed.value = false;
  usingFallback.value = false;
  reset();
});

// ── Zooming ──
const apply = (next, glide) => {
  animate.value = !!glide;
  state.value = next;
};
const setZoom = (nextZoom, point = {x: 0, y: 0}, glide = true) =>
  apply(zoomAt(state.value, nextZoom, point, fit.value, stageSize.value.width, stageSize.value.height), glide);
const zoomIn = () => setZoom(clampZoom(zoom.value * ZOOM_STEP));
const zoomOut = () => setZoom(clampZoom(zoom.value / ZOOM_STEP));
const fitImage = () => setZoom(ZOOM_MIN);

const pointFromEvent = (e) => {
  const rect = stage.value?.getBoundingClientRect();
  if (!rect) return {x: 0, y: 0};
  return {x: e.clientX - (rect.left + rect.width / 2), y: e.clientY - (rect.top + rect.height / 2)};
};
const onWheel = (e) => {
  if (!loaded.value) return;
  // A trackpad pinch arrives as a wheel with ctrlKey; both zoom about the pointer.
  setZoom(wheelZoom(zoom.value, e.deltaY), pointFromEvent(e), false);
};
const onKeydown = (e) => {
  if (!loaded.value) return;
  const next = keyZoom(e.key, zoom.value);
  if (next !== null) {
    e.preventDefault();
    setZoom(next);
    return;
  }
  const step = 48;
  const arrows = {ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step]};
  if (arrows[e.key]) {
    e.preventDefault();
    apply(panBy(state.value, ...arrows[e.key], fit.value, stageSize.value.width, stageSize.value.height), true);
  }
};

// ── Pointers: one drags, two pinch; a quick second tap or click toggles fit ↔ 2× about the point ──
const pointers = new Map();
let drag = null;
let pinch = null;
let lastTap = null;
let moved = 0;
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({x: (a.x + b.x) / 2, y: (a.y + b.y) / 2});
const onPointerDown = (e) => {
  if (!loaded.value || e.button > 0) return;
  e.preventDefault();
  stage.value?.setPointerCapture?.(e.pointerId);
  pointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
  moved = 0;
  if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    pinch = {distance: distance(a, b), zoom: zoom.value, mid: midpoint(a, b)};
    drag = null;
  } else if (pointers.size === 1) {
    drag = {x: e.clientX, y: e.clientY};
    dragging.value = true;
  }
};
const onPointerMove = (e) => {
  if (!pointers.has(e.pointerId)) return;
  const previous = pointers.get(e.pointerId);
  moved += Math.hypot(e.clientX - previous.x, e.clientY - previous.y);
  pointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
  if (pinch && pointers.size >= 2) {
    const [a, b] = [...pointers.values()];
    const mid = midpoint(a, b);
    const stageMid = pointFromEvent({clientX: mid.x, clientY: mid.y});
    const zoomed = zoomAt(state.value, pinchZoom(pinch.zoom, pinch.distance, distance(a, b)), stageMid, fit.value, stageSize.value.width, stageSize.value.height);
    apply(panBy(zoomed, mid.x - pinch.mid.x, mid.y - pinch.mid.y, fit.value, stageSize.value.width, stageSize.value.height), false);
    pinch.mid = mid;
  } else if (drag) {
    apply(panBy(state.value, e.clientX - drag.x, e.clientY - drag.y, fit.value, stageSize.value.width, stageSize.value.height), false);
    drag = {x: e.clientX, y: e.clientY};
  }
};
const onPointerUp = (e) => {
  if (!pointers.has(e.pointerId)) return;
  pointers.delete(e.pointerId);
  stage.value?.releasePointerCapture?.(e.pointerId);
  const tap = {time: e.timeStamp ?? Date.now(), x: e.clientX, y: e.clientY};
  if (pointers.size === 0) {
    const wasPinch = !!pinch;
    pinch = null;
    drag = null;
    dragging.value = false;
    if (!wasPinch && moved < 8) {
      if (isDoubleTap(lastTap, tap)) {
        lastTap = null;
        setZoom(toggleZoom(zoom.value), pointFromEvent(e));
      } else {
        lastTap = tap;
      }
    } else {
      lastTap = null;
    }
  } else if (pointers.size === 1) {
    // A finger lifted from a pinch: the other one carries on as a drag.
    pinch = null;
    const [rest] = [...pointers.values()];
    drag = {x: rest.x, y: rest.y};
  }
};
const onPointerCancel = (e) => {
  pointers.delete(e.pointerId);
  if (pointers.size === 0) {
    pinch = null;
    drag = null;
    dragging.value = false;
  }
};

</script>

<template>
  <tl-dialog
      :model-value="modelValue"
      :title="title"
      :max-width="1600"
      flush
      @update:model-value="emit('update:modelValue', $event)"
  >
    <template #head-actions>
      <div class="tliv-tools">
        <span class="tliv-zoom" aria-live="polite">{{ label }}</span>
        <tl-icon-btn tooltip="Zoom out" icon="mdi-minus" :disabled="!canZoomOut" @click="zoomOut"/>
        <tl-icon-btn tooltip="Zoom in" icon="mdi-plus" :disabled="!canZoomIn" @click="zoomIn"/>
        <v-btn variant="text" size="small" color="primary" class="tliv-fit" :disabled="!canZoomOut" @click="fitImage">Fit</v-btn>
      </div>
    </template>

    <div class="tliv-body">
      <div
          ref="stage"
          class="tliv-stage"
          :class="{'tliv-stage--zoomed': canZoomOut, 'tliv-stage--dragging': dragging, 'tliv-stage--animate': animate}"
          tabindex="0"
          role="group"
          :aria-label="`${title}. Zoom with + and −, 0 fits, arrows move.`"
          @wheel.prevent="onWheel"
          @keydown="onKeydown"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerCancel"
          @dblclick.prevent
          @dragstart.prevent
      >
        <img
            v-if="shownSrc && !failed"
            :key="shownSrc"
            class="tliv-img"
            :class="{'tliv-img--hidden': !loaded}"
            :src="shownSrc"
            :alt="alt"
            :style="imageStyle"
            draggable="false"
            @load="onLoad"
            @error="onError"
        >
        <div v-if="!loaded && !failed && shownSrc" class="tliv-state">
          <v-progress-circular indeterminate color="primary" size="28"/>
        </div>
        <div v-if="failed || !shownSrc" class="tliv-state">
          <v-icon size="40" class="tliv-state-icon">mdi-image-broken-variant</v-icon>
          <div class="tliv-state-t">The image couldn't be loaded</div>
        </div>
      </div>
      <div v-if="caption || $slots.caption" class="tliv-caption">
        <slot name="caption">{{ caption }}</slot>
      </div>
    </div>
  </tl-dialog>
</template>

<style scoped lang="scss">
.tliv-tools {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.tliv-zoom {
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  min-width: 34px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.tliv-fit {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
  min-width: 0;
}

.tliv-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Sized to the screen: the dialog is at most 100dvh − 48px tall and the head takes 53px; a caption scrolls under. */
.tliv-stage {
  position: relative;
  height: calc(100dvh - 120px);
  min-height: 220px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-light));
  cursor: zoom-in;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px rgb(var(--v-theme-primary));
  }

  &--zoomed {
    cursor: grab;
  }

  &--dragging {
    cursor: grabbing;
  }
}

.tliv-img {
  display: block;
  max-width: none;
  transform-origin: center center;
  will-change: transform;
  -webkit-user-drag: none;

  &--hidden {
    visibility: hidden;
  }
}

.tliv-stage--animate .tliv-img {
  transition: transform 0.18s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .tliv-stage--animate .tliv-img {
    transition: none;
  }
}

.tliv-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgb(var(--v-theme-textSubtle));
  pointer-events: none;
}

.tliv-state-icon {
  color: rgb(var(--v-theme-textSubtle));
}

.tliv-state-t {
  font-size: 13px;
}

.tliv-caption {
  padding: 10px 16px;
  font-size: 13px;
  line-height: 1.45;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

@media (max-width: 599.98px) {
  .tliv-zoom {
    display: none;
  }

}
</style>
